import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { WIDGET_LAYOUT } from '../../../../../../shared/interfaces/widgetLayout';
import { TOOLTIPS_NT } from '../../../../../../shared/data/tooltipsNT.data';
import { ALTERNATIVE_WIDGETS_NAME_NT } from '../../../../../../shared/data/alternativeWidgetsName.data';

// widgets that take 2 columns in the grid (same as the add widgets modal)
const WIDE_WIDGETS = [
  'widget-attivita',
  'widget-curva-accesso',
  'widget-curva-mercato',
  'widget-curva-mercato-30-giorni',
  'widget-curva-mercato-30-giorni-merged',
  'widget-curva-economia',
  'widget-evolution-regional-access',
  'widget-evolution-regional-economia',
  'widget-evolution-regional-market',
  'widget-evolution-regional-market-30-days',
  'widget-inclusion-process-tool-national',
];

@Component({
  selector: 'app-customize-national-report',
  imports: [CommonModule],
  templateUrl: './customize-national-report.component.html',
  styleUrl: './customize-national-report.component.css'
})
export class CustomizeNationalReportComponent {
  @Output() isModalOpen = new EventEmitter<boolean>();
  @Output() selectedOption = new EventEmitter<'all' | 'custom'>();
  @Output() selectedWidgets = new EventEmitter<number[]>();
  // widget-nHospitalsAndExperiences (id: 9) is not selectable in the report
  @Input() set widgets(widgets: WIDGET_LAYOUT[]) {
    this._widgets = widgets.filter((w) => w.id !== 9);
  }
  get widgets(): WIDGET_LAYOUT[] {
    return this._widgets;
  }
  private _widgets: WIDGET_LAYOUT[] = [];
  selected: 'all' | 'custom' | null = null;
  phase:number=0;
  selectedWidgetIds = new Set<number>();

  closeModal(): void {
    if (this.phase === 1) {
      this.saveSelectedWidgets();
    }
    this.phase=0;
    this.isModalOpen.emit(false);
  }

  continue(): void {
    if (this.selected=='all') {
      this.selectedOption.emit('all');
    }
    else if (this.selected=='custom') {
      this.loadSelectedWidgets();
      this.phase=1;
    }
    /* else if (this.selected=='custom') {
      this.isModalOpen.emit(false);
      this.selectedOption.emit('custom');
    }*/
  }

  back(): void {
    this.saveSelectedWidgets();
    this.phase=0;
  }

  isWidgetSelected(widget: WIDGET_LAYOUT): boolean {
    return this.selectedWidgetIds.has(widget.id);
  }

  toggleWidget(widget: WIDGET_LAYOUT): void {
    if (this.selectedWidgetIds.has(widget.id)) {
      this.selectedWidgetIds.delete(widget.id);
    } else {
      this.selectedWidgetIds.add(widget.id);
    }
  }

  get allWidgetsSelected(): boolean {
    return this.widgets.length > 0 && this.widgets.every((w) => this.selectedWidgetIds.has(w.id));
  }

  toggleSelectAll(): void {
    if (this.allWidgetsSelected) {
      this.selectedWidgetIds.clear();
    } else {
      this.selectedWidgetIds = new Set(this.widgets.map((w) => w.id));
    }
  }

  // alternative name only if chosen and existing
  getWidgetTitle(widget: WIDGET_LAYOUT): string {
    const alternativeTitle = ALTERNATIVE_WIDGETS_NAME_NT.find((w) => w.id === widget.id)?.text ?? '';
    return widget.isDefaultName === false && alternativeTitle !== '' ? alternativeTitle : widget.name;
  }

  getTooltipText(widgetId: number): string {
    return TOOLTIPS_NT.find((t) => t.id === widgetId)?.text ?? '';
  }

  isWide(widget: WIDGET_LAYOUT): boolean {
    return WIDE_WIDGETS.includes(widget.className);
  }

  getCardClasses(widget: WIDGET_LAYOUT): string {
    if (this.isWidgetSelected(widget)) {
      return 'border-dimot-1 shadow-md';
    }
    return 'border-gray-100 hover:shadow-md hover:-translate-y-0.5';
  }

  getCheckClasses(widget: WIDGET_LAYOUT): string {
    if (this.isWidgetSelected(widget)) {
      return 'bg-dimot-1 border-dimot-1 scale-100';
    }
    return 'bg-white/90 border-gray-300';
  }

  // normal cards: 2 lines of title + 4 of description, wide cards: 2 + 3
  getHoverPanelHeight(widget: WIDGET_LAYOUT): string {
    return this.isWide(widget) ? 'h-[5.9rem] 2xl:h-[6.55rem]' : 'h-[6.8rem] 2xl:h-[7.6rem]';
  }

  trackById(_index: number, widget: WIDGET_LAYOUT): number {
    return widget.id;
  }

  confirmWidgets(): void {
    const selectedWidgets = this.saveSelectedWidgets();
    this.selectedWidgets.emit(selectedWidgets.map((w) => w.id));
    this.selectedOption.emit('custom');
  }

  // stores the selected widgets (id, className, name) in the sessionStorage
  private saveSelectedWidgets() {
    const selectedWidgets = this.widgets
      .filter((w) => this.selectedWidgetIds.has(w.id))
      .map((w) => ({ id: w.id, className: w.className, name: w.name }));
    sessionStorage.setItem("nationalReportSelectedWidgets", JSON.stringify(selectedWidgets));
    return selectedWidgets;
  }

  // restores the selection from the sessionStorage, all widgets selected if nothing is stored
  private loadSelectedWidgets(): void {
    const availableIds = this.widgets.map((w) => w.id);
    let storedIds: number[] = [];
    try {
      const stored = JSON.parse(sessionStorage.getItem("nationalReportSelectedWidgets") || '[]');
      storedIds = stored
        .map((w: { id: number }) => w.id)
        .filter((id: number) => availableIds.includes(id));
    } catch {
      storedIds = [];
    }
    this.selectedWidgetIds = new Set(storedIds.length ? storedIds : availableIds);
  }
}
